import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday } from 'date-fns';

const Schedule: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'online' | 'in-person'>('all');

  // Mock data - in real app, this would come from API
  const events = [
    {
      id: '1',
      title: 'Portfolio Review Session',
      date: new Date('2025-08-20T14:00:00'),
      duration: 30,
      type: 'online' as const,
      reviewer: 'Sarah Johnson',
      status: 'scheduled' as const,
      availableSlots: 12,
    },
    {
      id: '2',
      title: 'Resume Workshop',
      date: new Date('2025-08-22T10:00:00'),
      duration: 45,
      type: 'in-person' as const,
      location: 'Design Building, Room 101',
      reviewer: 'Mike Chen',
      status: 'scheduled' as const,
      availableSlots: 8,
    },
    {
      id: '3',
      title: 'UX Design Critique',
      date: new Date('2025-08-25T15:30:00'),
      duration: 60,
      type: 'online' as const,
      reviewer: 'Emily Davis',
      status: 'available' as const,
      availableSlots: 15,
    },
  ];

  const getWeekDays = () => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    return eachDayOfInterval({ start, end });
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const days = direction === 'next' ? 7 : -7;
    setCurrentDate(addDays(currentDate, days));
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => isSameDay(event.date, date));
  };

  const filteredEvents = events.filter(event => {
    if (filterType === 'all') return true;
    return event.type === filterType;
  });

  const weekDays = getWeekDays();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Schedule</h1>
        <p className="text-gray-600 mt-2">View and book your portfolio review sessions</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* View Toggle */}
            <div className="flex items-center space-x-4">
              <div className="flex rounded-lg border border-gray-300 p-1">
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    viewMode === 'week'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    viewMode === 'month'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Month
                </button>
              </div>

              {/* Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as 'all' | 'online' | 'in-person')}
                  className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Events</option>
                  <option value="online">Online Only</option>
                  <option value="in-person">In-Person Only</option>
                </select>
              </div>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateWeek('prev')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
                {viewMode === 'week' 
                  ? `${format(weekDays[0], 'MMM d')} - ${format(weekDays[6], 'MMM d, yyyy')}`
                  : format(currentDate, 'MMMM yyyy')
                }
              </h2>
              <button
                onClick={() => navigateWeek('next')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Week View Calendar */}
        {viewMode === 'week' && (
          <div className="p-6">
            <div className="grid grid-cols-7 gap-4">
              {weekDays.map((day, index) => {
                const dayEvents = getEventsForDate(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isTodayDate = isToday(day);

                return (
                  <div key={index} className="min-h-[120px]">
                    <div className="text-center mb-2">
                      <div className="text-xs font-medium text-gray-500 uppercase">
                        {format(day, 'EEE')}
                      </div>
                      <button
                        onClick={() => setSelectedDate(day)}
                        className={`mt-1 w-8 h-8 rounded-full text-sm font-medium ${
                          isTodayDate
                            ? 'bg-primary-600 text-white'
                            : isSelected
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {format(day, 'd')}
                      </button>
                    </div>
                    <div className="space-y-1">
                      {dayEvents.map((event) => (
                        <div
                          key={event.id}
                          className={`p-2 rounded text-xs cursor-pointer ${
                            event.type === 'online'
                              ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {format(event.date, 'HH:mm')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Event List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filterType === 'all' ? 'All Events' : `${filterType === 'online' ? 'Online' : 'In-Person'} Events`}
          </h2>
        </div>
        <div className="p-6">
          {filteredEvents.length > 0 ? (
            <div className="space-y-4">
              {filteredEvents.map((event) => (
                <div key={event.id} className="border border-gray-200 rounded-lg p-6 hover:border-primary-300 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          event.type === 'online' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {event.type}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          event.status === 'scheduled'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {event.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {format(event.date, 'MMM d, yyyy')} at {format(event.date, 'h:mm a')}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2" />
                          {event.duration} minutes
                        </div>
                        {event.location && (
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-2" />
                            {event.location}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          Reviewer: {event.reviewer}
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          {event.availableSlots} slots available
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-6 flex flex-col space-y-2">
                      {event.status === 'available' ? (
                        <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                          Book Session
                        </button>
                      ) : (
                        <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                          View Details
                        </button>
                      )}
                      {event.status === 'scheduled' && (
                        <button className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No events found</h3>
              <p className="mt-2 text-gray-600">
                No events match your current filter. Try adjusting the filter or check back later.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Schedule;